import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToSection } from "@/components/Navbar";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

  const onTilt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="top" ref={sectionRef} className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-28" data-testid="hero-section">
      <motion.div className="absolute inset-0 -z-10" style={{ y: bgY }}>
        <img
          src="https://images.unsplash.com/photo-1515703944563-dbcfbf121b3d?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="h-[120%] w-full object-cover opacity-[0.14]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-canvas/60 via-canvas/80 to-canvas" />
      </motion.div>
      <div className="absolute -left-40 top-1/3 -z-10 h-[500px] w-[500px] rounded-full bg-accent/10 blur-[160px]" />

      <motion.div style={{ opacity: fade }} className="mx-auto grid w-full max-w-[1600px] grid-cols-1 items-center gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-accent sm:text-sm"
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-display text-[6.8vw] font-extrabold uppercase leading-[0.95] tracking-tight text-ink sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === t.hero.lines.length - 1 ? "text-accent" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
            data-testid="hero-subtext"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, "contact")}
              className="group flex items-center gap-2 rounded-full bg-accent px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas transition-transform duration-300 hover:scale-105"
              data-testid="hero-cta-primary"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#work"
              onClick={(e) => scrollToSection(e, "work")}
              className="rounded-full border border-white/20 px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-accent hover:text-accent"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-16 grid max-w-xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
          >
            {t.hero.stats.map((stat, i) => (
              <div key={i} className="border-l-2 border-accent/60 pl-4" data-testid={`hero-stat-${i}`}>
                <p className="font-display text-3xl font-extrabold leading-none text-ink sm:text-4xl">{stat.value}</p>
                <p className="mt-2 font-mono text-[10px] uppercase leading-snug tracking-[0.15em] text-mist sm:text-xs">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE }}
          className="hidden lg:col-span-4 lg:block"
        >
          <motion.div
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className="relative overflow-hidden rounded-2xl border border-white/10"
            data-testid="hero-visual"
          >
            <img
              src="https://images.unsplash.com/photo-1624918959325-4ab1f51306d1?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Noste studio workspace"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-canvas/90 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 font-mono text-xs uppercase tracking-[0.25em] text-accent">Built for growth.</p>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="pointer-events-none absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-dim sm:flex"
      >
        {t.hero.scroll}
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="h-3.5 w-3.5" />
        </motion.span>
      </motion.div>
    </section>
  );
};
